import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { FilterPopup } from '@storybook';
import { DatePicker } from 'components';
import { API_URL } from 'constant';
import { filteringTagsState, SelectedDateRangeState } from 'global-stores';
import { useNetwork, useNotification, useOutsideClick } from 'hooks';
import {
	Kebab,
	PageHead,
	Pager,
	SearchBox,
	StatusLegend,
	StatusChip,
	Tabs,
	useSyncedPulse,
} from 'views/design-system';
import { ComplexStepsView } from 'views/pipelines/components/complex-step-view';
import { SessionTreeGraph } from 'views/complex-onboarding-flow/components';

import { useSessionDetailsActions } from '../hooks/use-session-actions';
import {
	flowName,
	flowSteps,
	hasOfacWarning,
	fundLabel,
	primaryFund,
	stamp,
	toChain,
	toVerdict,
} from './derive';
import { SessionDrawer } from './session-drawer';
import { BundleDrawer } from './bundle-drawer';
import { useBundleCounts } from './use-session-bundle';
import { ITabKey, useDebounced, useSessionsDeck } from './use-sessions-deck';

import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
// The row draws the overview's diagram, and its styling lives with the modal —
// which is code-split, so on this route the stylesheet was absent entirely.
// Without it the node's label stayed `position: absolute` (an inline style the
// scss overrides), leaving the node 0px wide and react-flow with nothing to
// draw wires between: 8 nodes, 0 edges.
import 'views/pipelines/components/complex-step-view/complex-step-view.scss';
import './sessions-deck.scss';

const TABS: { key: ITabKey; label: string; tone?: 'warn' | 'mine' }[] = [
	{ key: 'all', label: 'All' },
	/* "Incomplete", not "Pending". These are sessions the APPLICANT has not
	   finished — nobody here is waiting on them. "Pending" read as a queue for
	   the reviewer, which is what Under Review actually is, so the two tabs
	   sounded like the same thing. The `pending` KEY is untouched: it is the
	   categorizedStatus the backend filters and counts on. */
	{ key: 'pending', label: 'Incomplete' },
	{ key: 'processing', label: 'Under Review' },
	{ key: 'rejected', label: 'Rejected' },
	{ key: 'completed', label: 'Approved' },
	{ key: 'amlWarning', label: 'Warning', tone: 'warn' },
	{ key: 'assigned', label: 'Assigned to me', tone: 'mine' },
];

export const SessionsDeck = () => {
	const [tab, setTab] = useState<ITabKey>('all');
	const [typed, setTyped] = useState('');
	const [sortBy, setSortBy] = useState<'createdAt' | 'updatedAt'>('updatedAt');
	const [archived, setArchived] = useState(false);
	const [page, setPage] = useState(1);
	const [picked, setPicked] = useState<string[]>([]);
	const [filterOpen, setFilterOpen] = useState(false);
	const filterRef = useRef<HTMLDivElement>(null);
	useOutsideClick(filterRef, () => setFilterOpen(false));

	// Both are shared atoms the existing filter popup and date picker write to.
	const dateRange = useRecoilValue(SelectedDateRangeState);
	const filteringTags = useRecoilValue(filteringTagsState);
	const [openSession, setOpenSession] = useState<any | null>(null);
	/* The bundle drawer is opened by the PILL, not by the row — clicking the row
	   still opens the session itself. Two different questions from one line. */
	const [bundleFor, setBundleFor] = useState<string | null>(null);

	const [flowSession, setFlowSession] = useState<any | null>(null);

	const search = useDebounced(typed);

	/* The amber "under review" marks — the node and the chip that names it —
	   beat together rather than each starting its own clock on mount. */
	useSyncedPulse();

	const { rows, counts, pages, loaded, error, reload } = useSessionsDeck({
		tab,
		search,
		sortBy,
		archived,
		page,
		startDate: dateRange?.[0]?.startDate,
		endDate: dateRange?.[0]?.endDate,
		filters: filteringTags,
	});
	/* One request per page, not one per row. See useBundleCounts for why the
	   session's own `isBundle` flag cannot drive this pill. */
	const { counts: bundleCounts, refresh: refreshBundleCounts } =
		useBundleCounts(useMemo(() => rows.map((r: any) => r._id), [rows]));


	/* KEEP THE OPEN DRAWER POINTED AT THE FRESH ROW.
	   `openSession` is the row object captured when the row was clicked, and the
	   drawer's chain pills, the chevron tree on the row behind it and the Tree
	   Flow modal are all drawn from it. Reloading the list alone replaces `rows`
	   and leaves the drawer holding the OLD snapshot, so a step just set to
	   Rejected keeps its previous colour. Re-point it at the reloaded row. */
	useEffect(() => {
		if (!openSession) return;
		const fresh = rows.find((r: any) => r?._id === openSession._id);
		if (fresh && fresh !== openSession) setOpenSession(fresh);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rows]);

	const activeFilters = useMemo(
		() => Object.keys(filteringTags ?? {}).filter(k => (filteringTags as any)[k]?.length).length,
		[filteringTags]
	);

	const { exportPdf } = useSessionDetailsActions();
	const { get, remove } = useNetwork({ updateState: false });
	const { successNotification, errorNotification } = useNotification();

	/* ------------------------------------------------------------- actions */

	const swap = useCallback((next: Partial<{ tab: ITabKey; sortBy: 'createdAt' | 'updatedAt'; archived: boolean }>) => {
		if (next.tab !== undefined) setTab(next.tab);
		if (next.sortBy !== undefined) setSortBy(next.sortBy);
		if (next.archived !== undefined) setArchived(next.archived);
		setPage(1);
		setPicked([]);
	}, []);

	const toggleRow = useCallback((id: string) => {
		setPicked(prev =>
			prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
		);
	}, []);

	const toggleAll = useCallback(
		(on: boolean) => setPicked(on ? rows.map((r: any) => r._id) : []),
		[rows]
	);

	const archive = useCallback(
		async (session: any) => {
			// eslint-disable-next-line no-alert
			if (!window.confirm(`${archived ? 'Unarchive' : 'Archive'} this session?`)) return;
			const resp = await remove(`${API_URL.COMPLEX_SESSION}/${session._id}`).catch(
				() => null
			);
			if (resp?.success || resp?._id) {
				successNotification(archived ? 'Session unarchived' : 'Session archived');
				reload();
				return;
			}
			errorNotification('Could not archive the session');
			// notification + network helpers churn identity every render.
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[archived, reload]
	);

	/**
	 * Re-invite is a plain GET against the session's own invite code — the same
	 * call the row's Re-invite link has always made. `type=complex` matters: the
	 * linear variant of the endpoint mints a different link shape.
	 */
	const reinvite = useCallback(
		async (session: any) => {
			if (!session?.code) {
				errorNotification('This session has no invite code to resend');
				return;
			}
			const fund = primaryFund(session.fundName);
			const resp = await get(
				`${API_URL.RESEND_INVITE}?code=${session.code}&resend=true&type=complex${
					fund ? `&companyName=${encodeURIComponent(fund)}` : ''
				}`
			).catch(() => null);
			if (resp?.data?.length > 0) successNotification('Invitation sent');
			else errorNotification('Could not resend the invitation');
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const showCode = useCallback(
		(session: any) => {
			const code = session?.code;
			if (!code) {
				errorNotification('This session has no access code');
				return;
			}
			navigator.clipboard?.writeText(code);
			successNotification(`Access code ${code} copied`);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const kebabFor = useCallback(
		(session: any) => [
			{ label: 'View complete flow', onClick: () => setFlowSession(session) },
			{ label: 'Download', onClick: () => exportPdf([session._id], 'pdf') },
			{ label: 'View Access Code', onClick: () => showCode(session) },
			{ label: archived ? 'Unarchive' : 'Archive', onClick: () => archive(session) },
		],
		[archive, archived, exportPdf, showCode]
	);

	/* --------------------------------------------------------------- render */

	const decorated = useMemo(
		() =>
			rows.map((session: any) => {
				const chain = toChain(session);
				return { session, chain, verdict: toVerdict(session, chain) };
			}),
		[rows]
	);

	const allPicked = rows.length > 0 && picked.length === rows.length;

	return (
		<div className="sv2-scope sv2 sessions-deck">
			<PageHead
				title="Sessions"
				sub={
					<>
						{counts.total.toLocaleString()} {archived ? 'archived' : 'active'} sessions
						&nbsp;&middot;&nbsp; page {page} of {pages} &nbsp;&middot;&nbsp; search matches
						phone, email, name and country
					</>
				}
			/>

			<StatusLegend />

			<div className="ctl sctl">
				<span
					className="pre"
					onClick={() =>
						swap({ sortBy: sortBy === 'updatedAt' ? 'createdAt' : 'updatedAt' })
					}
				>
					Sort: <b>{sortBy === 'updatedAt' ? 'Last updated' : 'Created'}</b> &#9662;
				</span>
				<span
					className={`pre${archived ? '' : ' on'}`}
					onClick={() => swap({ archived: !archived })}
				>
					{archived ? 'Archived' : 'Active'} &#9662;
				</span>
				<div ref={filterRef} className="sessions-deck__filter">
					<span
						className={`pre${activeFilters ? ' on' : ''}`}
						onClick={() => setFilterOpen(v => !v)}
					>
						Filter{activeFilters ? ` · ${activeFilters} applied` : ''}
					</span>
					{filterOpen && <FilterPopup filterPage="sessions" />}
				</div>

				<DatePicker stateType={SelectedDateRangeState} />
				<SearchBox
					value={typed}
					onChange={v => {
						setTyped(v);
						setPage(1);
					}}
					title="Searches phone, email, name and country"
				/>
				<Pager page={page} pages={pages} onChange={setPage} />
			</div>

			<Tabs
				tabs={TABS.map(t => ({ ...t, count: counts[t.key] }))}
				active={tab}
				onChange={k => swap({ tab: k as ITabKey })}
			/>

			{picked.length > 0 && (
				<div className="bulkbar">
					<b>{picked.length} Selected</b>
					<span className="bx" title="Clear selection" onClick={() => setPicked([])}>
						&#10005;
					</span>
					<span className="bact" onClick={() => exportPdf(picked, 'zip')}>
						Download Zip
					</span>
				</div>
			)}

			<div className="panel" style={{ marginTop: 12 }}>
				<div className="tscroll">
					<table className="stbl">
						<thead>
							<tr>
								<th className="ckcol">
									<input
										type="checkbox"
										aria-label="Select all"
										checked={allPicked}
										onChange={e => toggleAll(e.target.checked)}
									/>
								</th>
								<th className="odcol">Onboarding Details</th>
								<th className="flcol">Status &amp; Onboarding Flow</th>
								<th className="accol">Actions</th>
							</tr>
						</thead>
						<tbody>
							{decorated.map(({ session, verdict }) => (
								<tr
									key={session._id}
									className={`${verdict.edge}${
										openSession?._id === session._id ? ' sel' : ''
									}`}
									/* CLICKING THE OPEN ROW CLOSES IT.
									   The row is the only control for this drawer, so
									   without a toggle the way out is the × in the
									   corner — and the row a reviewer just clicked is
									   the nearest thing to hand. It already marks
									   itself `sel` while open, so it reads as pressed;
									   a pressed control that will not unpress is the
									   part that felt broken.

									   Compared on `_id`, not object identity: the
									   effect above re-points `openSession` at the
									   reloaded row whenever the list refreshes, so the
									   captured object is a different reference to the
									   same session within seconds of opening. */
									onClick={() => {
										/* ONE DRAWER AT A TIME. Both are fixed panels on
										   the same edge, so opening the second stacked it
										   on top of the first — two overlapping panels
										   about different things, with the one underneath
										   unreachable and no sign it was there. */
										setBundleFor(null);
										setOpenSession((prev: any) =>
											prev?._id === session._id ? null : session
										);
									}}
									title={
										openSession?._id === session._id
											? 'Close session details'
											: 'Open session details'
									}
								>
									<td className="ckcol">
										<input
											className="rowck"
											type="checkbox"
											checked={picked.includes(session._id)}
											onClick={e => e.stopPropagation()}
											onChange={() => toggleRow(session._id)}
										/>
									</td>

									<td className="od odcol">
										{/* THE FUND LINE ONLY WHEN IT SAYS SOMETHING THE NAME DOES NOT.
										    On these sessions `fundName` and `name` are the same string, so
										    the column opened with the same text twice — once small, then
										    again larger and bolder underneath. Suppressed on a match rather
										    than deleted outright, because on a real fund the two differ (a
										    company on one line, the applicant on the next) and dropping the
										    line unconditionally would lose which fund the session belongs
										    to. */}
										{fundLabel(session) &&
											fundLabel(session).toLowerCase() !==
												(session.name ?? '').trim().toLowerCase() && (
												<>
													<span className="fund">{fundLabel(session)}</span>
													<br />
												</>
											)}
										<span className="nm">{session.name || '--'}</span>
										<br />
										<span className="mail">{session.email || '--'}</span>
										<br />
										<span className="ph2">
											{session.contact
												? `${session.countryCode ?? ''} ${session.contact}`.trim()
												: '--'}
										</span>
										{/* THE SESSION ID, WHERE SOMEBODY CAN COPY IT.
											It is what every support conversation, log line and API call
											is keyed on, and until now the only way to see it was to open
											the drawer and read the "ID :" row — so anyone working from a
											ticket had to open sessions one by one to find the right one.
											`title` carries it too, so it survives being truncated. */}
										{session._id && (
											<>
												<br />
												<span className="sid" title={String(session._id)}>
													{/* Labelled the same way as the Created/Updated pair
														directly below, so the cell reads as one list of
														facts rather than a bare hex string floating
														between the phone and the timestamps. */}
													<b>id</b> : <span className="sidv">{String(session._id)}</span>
												</span>
											</>
										)}
										<div className="meta">
											<b>Created</b> : {stamp(session.createdAt)}
											<br />
											<b>Updated</b> : {stamp(session.updatedAt)}
										</div>
										{/* the verdict reads next to the bundle pill, not over the flow —
											they are both facts ABOUT the session, and the flow column is
											the diagram */}
										{/* STATUS FIRST, THEN THE BUNDLE PILL. The status is what the
											row is scanned for — it is the one thing that differs from
											row to row and the reason a reviewer stops on one. "Multi
											Session Bundle" is a fixed label that qualifies the session;
											leading with it pushed the status to a different horizontal
											position on every bundle row, so the column could not be read
											down. */}
										<div className="pills">
											<StatusChip verdict={verdict.verdict}>
												{verdict.label}
											</StatusChip>
											{(bundleCounts[session._id] ?? 0) > 0 && (
												/* A BUTTON, NOT A LABEL. It was a static chip that told
												   you a bundle existed and gave you no way to see it —
												   the reviewer's next question ("who else?") had no
												   answer on this screen. stopPropagation because the row
												   itself opens the session drawer, and a click here is
												   asking a different question. */
												<button
													type="button"
													className="msb msb--btn"
													onClick={e => {
														e.stopPropagation();
														/* Toggles like the row it sits on, and
														   closes the session drawer for the same
														   one-panel-at-a-time reason. */
														setOpenSession(null);
														setBundleFor(prev =>
															prev === session._id ? null : session._id
														);
													}}
													title="Show every session that looks like this person"
												>
													Multi Session Bundle
													<span className="msb__n">
														&middot; +{bundleCounts[session._id]}
													</span>
												</button>
											)}
										</div>
										{hasOfacWarning(session) && (
											<div className="ofac">Business &amp; OFAC sanction alert</div>
										)}
									</td>

									<td className="fl flcol">
										<div className="chain">
											{/* the same component the overview draws */}
											<div className="conditional-flow-steps rowflow">
												<SessionTreeGraph details={session} isSessionModal isSessionList />
											</div>
										</div>
										<div className="fnm">
											<span
												className="vcf"
												onClick={e => {
													e.stopPropagation();
													setFlowSession(session);
												}}
											>
												View complete flow
											</span>
											{flowName(session)}
											{flowSteps(session) ? ` — ${flowSteps(session)}` : ''}
										</div>
									</td>

									<td className="accol">
										<button
											className="reinv"
											type="button"
											onClick={e => {
												e.stopPropagation();
												reinvite(session);
											}}
										>
											Re-invite
										</button>
										<Kebab items={kebabFor(session)} />
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				{!loaded && <div className="sv2-loading">Loading sessions…</div>}
				{loaded && error && (
					<div className="sv2-empty">
						Could not load sessions.{' '}
						<span className="bact" onClick={reload}>
							Retry
						</span>
					</div>
				)}
				{loaded && !error && rows.length === 0 && (
					<div className="sv2-empty">No sessions match this filter.</div>
				)}
			</div>

			<div className="foot">
				Full step names are always shown &mdash; nothing truncates to &ldquo;Entity/KYB
				Do&hellip;&rdquo;. The coloured left edge mirrors the session status (green approved
				&middot; amber under review &middot; red rejected &middot; purple started) so the queue
				scans without reading. Click a row to open the session detail drawer.
			</div>

			<BundleDrawer
				sessionId={bundleFor}
				open={!!bundleFor}
				/* A verdict changes the bundle's size, so the pill behind the
				   drawer has to be re-read — otherwise the list keeps advertising
				   a count the reviewer has already ruled down. */
				onChanged={refreshBundleCounts}
				onClose={() => setBundleFor(null)}
				onOpenSession={picked => {
					/* A DEAD BUTTON FOR FIVE ROWS OUT OF SIX.
					   This looked up the session in `rows` — the current PAGE of
					   the list — and silently did nothing when it was absent. It
					   is absent almost always: the whole point of a bundle is
					   that the other sessions are elsewhere in the queue, and
					   several of them are not returned by the list at all. The
					   bundle drawer closed and nothing opened in its place.

					   The list row is still preferred when there is one, because
					   it carries fields the bundle payload does not (fund, flow
					   design, assignee). Otherwise the bundle's own row is enough:
					   SessionDrawer fetches everything substantial from
					   `complex-session-details` keyed on `_id`. */
					const fromList = rows.find((r: any) => r._id === picked._id);
					setBundleFor(null);
					setOpenSession(
						fromList ?? {
							_id: picked._id,
							name: picked.name,
							email: picked.email,
							contact: picked.phone,
							status: picked.status,
							createdAt: picked.createdAt,
							updatedAt: picked.updatedAt,
						}
					);
				}}
			/>

			<SessionDrawer
				session={openSession}
				open={!!openSession}
				onChanged={reload}
				onClose={() => setOpenSession(null)}
				onViewFlow={setFlowSession}
				onDownload={(session, fileType) => exportPdf([session._id], fileType)}
			/>

			{flowSession && (
				<ComplexStepsView
					isOpen={!!flowSession}
					closeModal={() => setFlowSession(null)}
					details={flowSession}
				/>
			)}
		</div>
	);
};

export default SessionsDeck;
